import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

const BACKEND_URL = '';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link.');
      return;
    }

    fetch(`${BACKEND_URL}/api/auth/verify-email?token=${token}`)
      .then(async res => {
        const data = await res.json();
        if (res.ok) {
          setStatus('success');
          setMessage(data.message || 'Email verified successfully!');
        } else {
          setStatus('error');
          setMessage(data.detail || 'Verification failed.');
        }
      })
      .catch(() => {
        setStatus('error');
        setMessage('Something went wrong. Please try again.');
      });
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="verify-email-page">
      <Card className="p-8 max-w-md w-full text-center">
        {status === 'loading' && (
          <div data-testid="verify-email-loading">
            <Loader2 className="h-10 w-10 text-brand-navy animate-spin mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-slate-800 mb-2">Verifying your email...</h2>
            <p className="text-sm text-slate-500">Please wait a moment.</p>
          </div>
        )}
        {status === 'success' && (
          <div data-testid="verify-email-success">
            <div className="w-14 h-14 bg-brand-sage/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-7 w-7 text-brand-sage" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800 mb-2">Email Verified!</h2>
            <p className="text-sm text-slate-500 mb-4">{message}</p>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-6 text-left">
              <p className="text-[11px] text-slate-500 leading-relaxed">
                <span className="font-semibold text-slate-600">Privacy & GDPR:</span> Your consent to our data privacy policy has been recorded. You may exercise your data rights at any time by contacting sll@britehub.community.
              </p>
            </div>
            <Button
              data-testid="verify-go-to-login-btn"
              onClick={() => navigate('/login')}
              className="bg-gradient-to-r from-brand-navy to-brand-sage text-white"
            >
              Go to Login
            </Button>
          </div>
        )}
        {status === 'error' && (
          <div data-testid="verify-email-error">
            <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="h-7 w-7 text-red-500" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800 mb-2">Verification Failed</h2>
            <p className="text-sm text-slate-500 mb-6">{message}</p>
            <Button
              data-testid="verify-go-to-login-btn"
              onClick={() => navigate('/login')}
              variant="outline"
            >
              Back to Login
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
